import { useState, useEffect } from "react";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "./ui/resizable";
import { Button } from "./ui/button";
import { PanelLeft, PanelRight } from "lucide-react";
import ReaderLeftPanel from "./ReaderLeftPanel";

/**
 * Desktop Reader Shell — "Beit Midrash" 3-panel layout.
 *
 *   ┌────────────┬──────────────────────────┬──────────────┐
 *   │   LEFT     │          CENTER           │    RIGHT     │
 *   │  Nav / TOC │      3D book / study      │ Commentary / │
 *   │  Search    │                           │ AI / Notes   │
 *   │  Bookmarks │                           │              │
 *   └────────────┴──────────────────────────┴──────────────┘
 *
 * Both side panels are collapsible and resizable. Sizes are persisted to
 * localStorage so returning users keep their workspace layout.
 */
export default function DesktopReaderShell({
  book,
  slug,
  currentChapter,
  searchInputRef,
  rightPanel,
  rightPanelOpen,
  children,
}) {
  const [leftOpen, setLeftOpen] = useState(() => {
    try {
      const v = localStorage.getItem("tanakh_left_panel_open");
      return v === null ? true : v === "true";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("tanakh_left_panel_open", String(leftOpen));
    } catch {
      /* noop */
    }
  }, [leftOpen]);

  const showRight = rightPanelOpen && !!rightPanel;

  return (
    <div
      data-testid="desktop-reader-shell"
      className="desktop-reader-shell relative flex-1 min-h-0"
    >
      {/* Collapsed-left rail toggle */}
      {!leftOpen && (
        <button
          type="button"
          data-testid="desktop-left-rail-toggle"
          onClick={() => setLeftOpen(true)}
          className="absolute top-4 right-4 z-20 inline-flex items-center gap-1.5 rounded-sm border border-gold/30 bg-card/80 px-2.5 py-1.5 text-xs text-foreground hover:border-gold hover:text-gold transition-colors backdrop-blur"
          aria-label="פתח כלי למידה מתקדמים"
          title="כלי למידה מתקדמים"
        >
          <PanelRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          כלים מתקדמים
        </button>
      )}

      <ResizablePanelGroup
        direction="horizontal"
        autoSaveId="tanakh-reader-layout"
        className="h-full w-full"
      >
        {/* LEFT navigator panel */}
        {leftOpen && (
          <>
            <ResizablePanel
              defaultSize={18}
              minSize={14}
              maxSize={28}
              id="reader-left"
              order={1}
            >
              <ReaderLeftPanel
                book={book}
                slug={slug}
                currentChapter={currentChapter}
                searchInputRef={searchInputRef}
                onClose={() => setLeftOpen(false)}
              />
            </ResizablePanel>
            <ResizableHandle withHandle />
          </>
        )}

        {/* CENTER — the book / study content */}
        <ResizablePanel
          defaultSize={showRight ? 54 : (leftOpen ? 82 : 100)}
          minSize={40}
          id="reader-center"
          order={2}
          className="relative"
        >
          <div
            className="h-full w-full overflow-y-auto"
            data-testid="desktop-reader-center"
          >
            {children}
          </div>
        </ResizablePanel>

        {/* RIGHT — verse detail / commentary / AI / notes */}
        {showRight && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel
              defaultSize={28}
              minSize={22}
              maxSize={44}
              id="reader-right"
              order={3}
            >
              <div
                className="h-full w-full border-s border-gold/20 bg-card/30"
                data-testid="desktop-reader-right"
              >
                {rightPanel}
              </div>
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
    </div>
  );
}
